/**
 * Aggregate axis-level measurements into a single marker-suitability verdict.
 *
 * Thresholds are heuristic but calibrated against a small corpus of real
 * markers (good/ok/bad) so they reflect what trackers tolerate in practice
 * rather than ideal-world numbers. Tune as we gather more field data.
 */

import type {
  ImageProbeReport,
  MarkerAxisName,
  MarkerVerdict,
  MarkerVerdictAxis,
  VerdictStatus,
} from './types';

/** Sort order green > yellow > red so the worst axes float to the top. */
const STATUS_RANK: Record<VerdictStatus, number> = { green: 0, yellow: 1, red: 2 };

/**
 * Axes where a single red pulls the overall verdict to red — losing these
 * makes the marker unusable, not just degraded. The rest can be yellow-flagged
 * individually without dooming the whole image.
 */
const CRITICAL_AXES: ReadonlySet<MarkerAxisName> = new Set(['features', 'blur', 'aspect']);

export function evaluateMarker(report: ImageProbeReport): MarkerVerdict {
  const axes: MarkerVerdictAxis[] = [];

  axes.push(resolutionAxis(report));
  axes.push(aspectAxis(report));

  if (report.features) {
    axes.push(featuresAxis(report.features.keypointCount));
    axes.push(distributionAxis(report.features.distribution));
  }
  if (report.uniqueness) {
    axes.push(uniquenessAxis(report.uniqueness.selfMatchRatio));
  }
  if (report.quality) {
    axes.push(contrastAxis(report.quality.contrast));
    axes.push(exposureAxis(report.quality.clippedShadowFraction, report.quality.clippedHighlightFraction));
    axes.push(blurAxis(report.quality.laplacianVariance));
  }

  const overall = rollupStatus(axes);
  return {
    overall,
    axes: [...axes].sort((a, b) => STATUS_RANK[b.status] - STATUS_RANK[a.status]),
    headline: headlineFor(overall),
  };
}

function resolutionAxis(report: ImageProbeReport): MarkerVerdictAxis {
  // 512 is the trace editor's default marker export size. Don't penalize it.
  const shortEdge = Math.min(report.file.width, report.file.height);
  const status: VerdictStatus = shortEdge >= 512 ? 'green' : shortEdge >= 256 ? 'yellow' : 'red';
  return {
    axis: 'resolution',
    status,
    actual: `${report.file.width} × ${report.file.height}`,
    target: '≥ 512 px on the short edge',
    explanation: status === 'green'
      ? 'Plenty of pixels for feature extraction.'
      : status === 'yellow'
        ? 'Workable, but a higher-resolution source will track more reliably.'
        : 'Too small — trackers struggle to find distinct features below ~256 px.',
  };
}

function aspectAxis(report: ImageProbeReport): MarkerVerdictAxis {
  const { width, height } = report.file;
  if (width === 0 || height === 0) {
    return axisRed('aspect', `${width} × ${height}`, 'between 1:2 and 2:1', 'Image has zero size on one axis.');
  }
  const ratio = width >= height ? width / height : height / width;
  const status: VerdictStatus = ratio <= 2 ? 'green' : ratio <= 3 ? 'yellow' : 'red';
  return {
    axis: 'aspect',
    status,
    actual: ratio.toFixed(2) + ':1',
    target: '≤ 2:1',
    explanation: status === 'green'
      ? 'Reasonable aspect ratio for marker tracking.'
      : status === 'yellow'
        ? 'Very wide or tall markers leave little surface area to track from a typical viewing angle.'
        : 'Extreme aspect ratio makes pose estimation unreliable.',
  };
}

function featuresAxis(count: number): MarkerVerdictAxis {
  const status: VerdictStatus = count >= 400 ? 'green' : count >= 150 ? 'yellow' : 'red';
  return {
    axis: 'features',
    status,
    actual: `${count} keypoints`,
    target: '≥ 400 ORB keypoints',
    explanation: status === 'green'
      ? 'Rich in trackable detail.'
      : status === 'yellow'
        ? 'Some trackable detail, but the tracker may lose lock under motion.'
        : 'Not enough distinct corners or edges — large flat or smooth regions.',
  };
}

function distributionAxis(distribution: number): MarkerVerdictAxis {
  // When the total feature count is high (we cap at 1500), even 60% grid
  // coverage means hundreds of features across most of the image. Reserve
  // red for genuinely clustered cases (≤35%).
  const pct = Math.round(distribution * 100);
  const status: VerdictStatus = distribution >= 0.6 ? 'green' : distribution >= 0.35 ? 'yellow' : 'red';
  return {
    axis: 'distribution',
    status,
    actual: `${pct}% grid coverage`,
    target: '≥ 60%',
    explanation: status === 'green'
      ? 'Features are spread across the image.'
      : status === 'yellow'
        ? 'Features cluster in one region — tracking may degrade when that region is occluded or off-frame.'
        : 'Features are concentrated in a small area. Most of the marker offers nothing to track.',
  };
}

function uniquenessAxis(selfMatchRatio: number): MarkerVerdictAxis {
  // Real-world "ok" markers (paintings, scenery) commonly land in the
  // 5–15% range because natural images have some repeating texture. Reserve
  // red for clearly repetitive content (>30%).
  const pct = Math.round(selfMatchRatio * 100);
  const status: VerdictStatus = selfMatchRatio <= 0.15 ? 'green' : selfMatchRatio <= 0.3 ? 'yellow' : 'red';
  return {
    axis: 'uniqueness',
    status,
    actual: `${pct}% repeating patches`,
    target: '≤ 15%',
    explanation: status === 'green'
      ? 'Patches are visually distinct from one another.'
      : status === 'yellow'
        ? 'Some repetition — trackers may confuse one region for another under perspective change.'
        : 'Strongly repeating texture (grids, stripes, periodic patterns). Pose can lock onto the wrong instance.',
  };
}

function contrastAxis(contrast: number): MarkerVerdictAxis {
  const status: VerdictStatus = contrast >= 50 ? 'green' : contrast >= 30 ? 'yellow' : 'red';
  return {
    axis: 'contrast',
    status,
    actual: contrast.toFixed(1),
    target: 'luma stddev ≥ 50',
    explanation: status === 'green'
      ? 'Strong tonal range.'
      : status === 'yellow'
        ? 'Somewhat flat — punching up contrast helps feature detection.'
        : 'Very low contrast. Edges and corners may be too soft to detect reliably.',
  };
}

function exposureAxis(shadows: number, highlights: number): MarkerVerdictAxis {
  // Large flat regions of sky/white-background are common in real photos and
  // don't kill tracking as long as the rest of the image carries enough
  // features. Only flag red when the majority of pixels are clipped.
  const clipped = shadows + highlights;
  const pct = Math.round(clipped * 100);
  const status: VerdictStatus = clipped <= 0.15 ? 'green' : clipped <= 0.45 ? 'yellow' : 'red';
  return {
    axis: 'exposure',
    status,
    actual: `${pct}% clipped`,
    target: '≤ 15% clipped',
    explanation: status === 'green'
      ? 'Well-exposed across the tonal range.'
      : status === 'yellow'
        ? 'Some crushed shadows or blown highlights — those regions contribute no features.'
        : 'Most of the image is blown or crushed. Those pixels are invisible to the tracker.',
  };
}

function blurAxis(laplacianVariance: number): MarkerVerdictAxis {
  const status: VerdictStatus = laplacianVariance >= 200 ? 'green' : laplacianVariance >= 80 ? 'yellow' : 'red';
  return {
    axis: 'blur',
    status,
    actual: laplacianVariance.toFixed(0),
    target: 'variance of Laplacian ≥ 200',
    explanation: status === 'green'
      ? 'Sharp focus.'
      : status === 'yellow'
        ? 'Slightly soft — passes, but a sharper source is better.'
        : 'Image looks blurry. Marker trackers depend on crisp edges.',
  };
}

function axisRed(axis: MarkerAxisName, actual: string, target: string, explanation: string): MarkerVerdictAxis {
  return { axis, status: 'red', actual, target, explanation };
}

/**
 * Roll axes up to one verdict.
 *
 * - **Red** if any critical axis (features/blur/aspect) is red, OR if three
 *   or more non-critical axes are red. Trackers degrade gracefully — a
 *   stylized illustration can have clipped highlights AND repetition AND
 *   still track if features, sharpness, and aspect are all solid.
 * - **Yellow** if no critical reds but at least one axis is non-green.
 * - **Green** if every axis is green.
 */
function rollupStatus(axes: MarkerVerdictAxis[]): VerdictStatus {
  let reds = 0;
  let yellows = 0;
  let criticalRed = false;
  for (const a of axes) {
    if (a.status === 'red') {
      reds++;
      if (CRITICAL_AXES.has(a.axis)) criticalRed = true;
    } else if (a.status === 'yellow') {
      yellows++;
    }
  }
  if (criticalRed || reds >= 3) return 'red';
  if (reds >= 1 || yellows >= 1) return 'yellow';
  return 'green';
}

function headlineFor(status: VerdictStatus): string {
  return status === 'green'
    ? 'Strong marker candidate'
    : status === 'yellow'
      ? 'Usable, with caveats'
      : 'Likely to track poorly';
}
